import React, { useState, useEffect, useCallback } from 'react';
import { Form } from '@rocketseat/unform';
import { useDispatch, useSelector } from 'react-redux';
import { MdSearch, MdFilterList } from 'react-icons/md';
import {
  WrapperFlex,
  Title,
  Breadcrumbs,
  SearchBox,
} from '@unimed-fesp/components';

import 'react-toastify/dist/ReactToastify.css';

import { Container } from './styles';

import theme from '../../styles/theme';
import FadeIn from '../../components/atoms/FadeIn';

import history from '../../services/history';
import FilterExtendsMassaInicial from '../../components/organisms/FilterExtendsMassaInicial';
import TabTableListasMassaInicial from '../../components/organisms/TabTableListasMassaInicial';
import ModalCancelAction from '../../components/organisms/ModalCancelAction';

import { Creators as ListaActions } from '../../store/ducks/lista';
import prepareBase64File from '../../utils/prepareBase64File';

const modelFilters = {
  cnpj: '',
  nomeEmpresa: '',
  nomeResponsavel: '',
  email: '',
  dataRegistroDe: null,
  dataRegistroAte: null,
};

const paginationTabParams = {
  pageNumber: 1,
  pageSize: 10,
  nextPage: null,
  previousPage: null,
  totalPages: null,
};

function ListasMassaInicial() {
  const dispatch = useDispatch();
  const { getArquivoMassaInicial } = useSelector((state) => state.lista);

  const [filters, setFilters] = React.useState(modelFilters);

  const [openFilterExtends, setOpenFilterExtends] = useState(false);
  const [cancelFilterExtendsOpen, setCancelFilterExtendsOpen] = useState(false);
  const [filtersParams, setFiltersParams] = React.useState(modelFilters);
  const [paginationParams, setPaginationParams] = useState(paginationTabParams);
  const [openModalCancelAction, setOpenModalCancelAction] = useState({
    warning: false,
    message: '',
    onClickOk: null,
  });

  function handleFilterExtends() {
    setOpenFilterExtends(!openFilterExtends);
  }

  function handleSubmit() {
    setFiltersParams({ ...filters });
    setPaginationParams({ ...paginationParams, pageNumber: 1, pageSize: 10 });
  }

  const handleGetListaMassaInicial = useCallback(() => {
    const { pageNumber, pageSize } = paginationParams;
    let { cnpj, dataRegistroAte, dataRegistroDe } = filtersParams;

    if (cnpj) {
      cnpj = cnpj.replaceAll(/[^\d]/g, '');
    }
    if (dataRegistroAte) {
      dataRegistroAte = dataRegistroAte.toJSON();
    }
    if (dataRegistroDe) {
      dataRegistroDe = dataRegistroDe.toJSON();
    }

    dispatch(
      ListaActions.getListasMassaInicialRequest({
        ...filtersParams,
        cnpj,
        dataRegistroAte,
        dataRegistroDe,
        pageNumber,
        pageSize,
      })
    );
  }, [dispatch, paginationParams, filtersParams]);

  function handleInputKeyDown(e) {
    if (e.keyCode === 13) {
      e.preventDefault();
      handleSubmit();
    }
  }

  function handleModalCancelAction() {
    setOpenModalCancelAction({
      warning: !openModalCancelAction,
    });
  }

  function downloadMassaInicial(idLista, idArquivo, nomeArquivoDownload) {
    dispatch(
      ListaActions.getArquivoMassaInicialRequest(
        idLista,
        idArquivo,
        nomeArquivoDownload
      )
    );
  }

  function downloadTermoResponsabilidade(
    idLista,
    idArquivo,
    nomeArquivoDownload
  ) {
    dispatch(
      ListaActions.getTermoResponsabilidadeMassaInicialRequest(
        idLista,
        nomeArquivoDownload
      )
    );
  }

  function handleDownloadFile(
    tipoAcao,
    idLista,
    idArquivo,
    nomeArquivoDownload
  ) {
    const onClickOKDownload =
      tipoAcao === 'massa_inicial'
        ? downloadMassaInicial
        : downloadTermoResponsabilidade;

    setOpenModalCancelAction({
      warning: !openModalCancelAction.warning,
      message:
        tipoAcao === 'massa_inicial'
          ? 'Deseja fazer o download da massa inicial?'
          : 'Deseja fazer o download do termo de responsabilidade?',
      textHeader: 'atenção!',
      onClickOk: () =>
        onClickOKDownload(idLista, idArquivo, nomeArquivoDownload),
    });
  }

  function downloadFile(base64, extension, nomeArquivoDownload) {
    const temp = prepareBase64File(extension, base64);

    const download = document.createElement('a');
    download.href = temp;
    download.download = `${nomeArquivoDownload}${extension}`;
    document.body.appendChild(download);
    download.click();
    document.body.removeChild(download);
  }

  function handleLimparFiltros() {
    setFilters(modelFilters);
    setFiltersParams(modelFilters);
  }

  useEffect(() => {
    if (!getArquivoMassaInicial.loading && getArquivoMassaInicial.success) {
      if (getArquivoMassaInicial.response.arquivo !== '') {
        downloadFile(
          getArquivoMassaInicial.response.arquivo,
          getArquivoMassaInicial.response.extensaoArquivo,
          getArquivoMassaInicial.response.nomeArquivoDownload
        );
        setOpenModalCancelAction((state) => ({ ...state, warning: false }));
      }
    }
  }, [getArquivoMassaInicial.loading, getArquivoMassaInicial.response]);

  useEffect(() => {
    handleGetListaMassaInicial();
  }, [handleGetListaMassaInicial]);

  return (
    <Container>
      <FadeIn>
        <WrapperFlex justifyStart alignStart column>
          <WrapperFlex fullWidth justifySpaceBetween alignStart>
            <Title
              text="listas de massa inicial"
              color={theme.titleGray}
              fontSize={38}
              marginBottom={8}
            />
          </WrapperFlex>
          <Breadcrumbs
            crumbs={[
              { path: 'novo' },
              {
                path: 'listas massa inicial',
                onNavigate: () => history.push('/novo/listas-massa-inicial'),
              },
            ]}
          />
        </WrapperFlex>
        <Form onSubmit={handleSubmit}>
          <WrapperFlex justifyStart alignStart row>
            <SearchBox
              onClick={handleFilterExtends}
              isPressed={openFilterExtends}
              placeholder="Pesquise pelo nome da empresa"
              onChange={({ target }) =>
                setFilters((filtersOld) => ({
                  ...filtersOld,
                  nomeEmpresa: target.value,
                }))
              }
              value={filters.nomeEmpresa}
              onKeyDown={handleInputKeyDown}
              cancelFilterExtendsOpen={cancelFilterExtendsOpen}
              IconFilter={MdFilterList}
              IconSearch={MdSearch}
            >
              <WrapperFlex marginTop={25} justifyStart alignStart row>
                <FilterExtendsMassaInicial
                  isOpen
                  filters={filters}
                  setFilters={setFilters}
                  handleLimparFiltros={handleLimparFiltros}
                />
              </WrapperFlex>
            </SearchBox>
          </WrapperFlex>
        </Form>
        <WrapperFlex marginTop={28} justifyStart alignStart row>
          <TabTableListasMassaInicial
            setPaginationParams={setPaginationParams}
            paginationParams={paginationParams}
            handleDownloadFile={handleDownloadFile}
            handleGetListaMassaInicial={handleGetListaMassaInicial}
          />
        </WrapperFlex>
      </FadeIn>
      <ModalCancelAction
        text={openModalCancelAction.message}
        onClick={handleModalCancelAction}
        textHeader={openModalCancelAction.textHeader}
        onClickOk={openModalCancelAction.onClickOk}
        isOpen={openModalCancelAction.warning}
      />
    </Container>
  );
}

export default ListasMassaInicial;
